import { useEffect, useState } from 'react';
import PillTabs from '../components/PillTabs';
import Surface from '../components/ui/Surface';
import Button from '../components/ui/Button';

export const SERVICES = [
  { id: 'financial-planning', label: 'Financial Planning' },
  { id: 'tax-planning', label: 'Tax Planning & Preparation' },
  { id: 'investment-management', label: 'Investment Management' },
  { id: 'advanced-wealth', label: 'Advanced Wealth Strategies' },
  { id: 'generational-wealth', label: 'Generational Wealth Management' },
  { id: 'custom-hnw', label: 'Custom High Net Worth Solutions' }
];

const COPY = {
  'financial-planning': {
    title: 'Financial Planning',
    intro:
      'Clarity first. We build a coordinated plan that translates goals and values into sequence, timelines, and next actions.',
    help: [
      'Cash-flow design and account structure that keeps priorities funded.',
      'Goal mapping: retirement income, education, real estate, and big purchases.',
      'Protection basics: beneficiaries, titling, estate document coordination.',
      'Portfolio risk alignment and savings targets by account type.',
      'Annual planning cadence with clear deliverables and accountability.'
    ],
    why: [
      'A written plan reduces noise and improves follow-through.',
      'Coordination across accounts helps avoid expensive gaps or overlaps.',
      'Clear next steps create momentum and measurable progress.'
    ],
    cta: { label: 'See how planning would look for you', href: '/contact' }
  },

  'tax-planning': {
    title: 'Tax Planning & Preparation',
    intro:
      'Taxes are one of the levers you can control. We integrate year-round tax planning with preparation to help keep more of what you earn.',
    help: [
      'Annual projection and withholding/estimate guidance.',
      'Tax-aware savings and withdrawal sequencing (IRA, Roth, HSA, brokerage).',
      'Equity comp strategies (RSUs, ISOs/NSOs, ESPP) with AMT awareness.',
      'Harvesting and asset location to enhance after-tax outcomes.',
      'Coordinated preparation and filing with your CAM planner and tax pro.'
    ],
    why: [
      'Planning throughout the year often beats a once-a-year scramble.',
      'Tax placement and timing can materially affect long-term wealth.',
      'One coordinated team reduces rework and surprises.'
    ],
    cta: { label: 'Talk through your tax strategy', href: '/contact' }
  },

  'investment-management': {
    title: 'Investment Management',
    intro:
      'Evidence-based portfolios aligned with your goals, time horizon, and risk comfort—implemented with discipline.',
    help: [
      'Investment Policy Statement (IPS) tailored to objectives and constraints.',
      'Globally diversified, cost-aware portfolio construction.',
      'Ongoing monitoring, rebalancing, and risk management.',
      'Tax-aware implementation: asset location and efficient transitions.',
      'Transparent reporting and progress reviews.'
    ],
    why: [
      'Process and rules help remove emotion from decisions.',
      'Diversification and costs matter—consistently.',
      'Coordination with planning keeps investments serving the plan, not the other way around.'
    ],
    cta: { label: 'Get an IPS built for your goals', href: '/contact' }
  },

  'advanced-wealth': {
    title: 'Advanced Wealth Strategies',
    intro:
      'For complex balance sheets: entity design, equity comp, liquidity events, and multi-stakeholder decisions.',
    help: [
      'Concentrated-stock and liquidity planning (10b5-1, collars, diversification).',
      'Charitable strategies (DAFs, appreciated asset giving, CRT exploration).',
      'Business owner coordination: retirement plans, cash balance, exit readiness.',
      'Asset protection and titling coordination with counsel.',
      'Scenario modeling to weigh trade-offs before acting.'
    ],
    why: [
      'Complex choices benefit from structured modeling and coordination.',
      'Early design often creates better after-tax outcomes.',
      'A single point of accountability reduces friction across advisors.'
    ],
    cta: { label: 'Explore advanced options with a planner', href: '/contact' }
  },

  'generational-wealth': {
    title: 'Generational Wealth Management',
    intro:
      'Align wealth with family values across lifetimes—clear governance, education, and graceful transitions.',
    help: [
      'Family meetings and decision frameworks that reduce uncertainty.',
      'Education for next-gen stewards on investing, risk, and values.',
      'Beneficiary and trustee coordination with estate counsel.',
      'Tax-aware gifting and legacy funding strategies.',
      'Ongoing monitoring as family, markets, and laws change.'
    ],
    why: [
      'Shared language reduces conflict and improves decisions.',
      'Prepared heirs are more likely to preserve and grow family capital.',
      'A plan turns intent into actions that last.'
    ],
    cta: { label: 'Design your family’s governance rhythm', href: '/contact' }
  },

  'custom-hnw': {
    title: 'Custom High Net Worth Solutions',
    intro:
      'White-glove planning and implementation for complex households and institutions.',
    help: [
      'Bespoke portfolio mandates and manager coordination.',
      'Private strategies diligence and fit assessment (as appropriate).',
      'Multi-advisor and banking relationship orchestration.',
      'Liquidity, lending, and concentrated position solutions.',
      'Tailored reporting for families, trustees, and investment committees.'
    ],
    why: [
      'Customization should still feel simple and accountable.',
      'A single architecture helps avoid duplication and blind spots.',
      'You get leverage from a team that speaks the same language as your partners.'
    ],
    cta: { label: 'Schedule a confidential consultation', href: '/contact' }
  }
};

function ServicePanel({ id }) {
  const service = COPY[id];
  if (!service) {
    return null;
  }

  return (
    <div
      id={`panel-${id}`}
      role="tabpanel"
      aria-labelledby={`tab-${id}`}
      className="mt-8 transition-opacity duration-200"
    >
      <Surface className="p-6 md:p-8">
        <div className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h3 className="text-lg md:text-2xl font-semibold text-slate-900">{service.title}</h3>
            <p className="mt-2 text-slate-600">{service.intro}</p>

            <h4 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-700">How we help</h4>
            <ul className="mt-2 list-disc space-y-2 pl-5 text-slate-700">
              {service.help.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h4 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-700">Why this matters</h4>
            <ul className="mt-2 list-disc space-y-2 pl-5 text-slate-700">
              {service.why.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <div className="mt-8">
              <Button
                href={service.cta.href}
                variant="primary"
                className="w-full sm:w-auto hover:shadow-md hover:-translate-y-0.5"
              >
                {service.cta.label}
              </Button>
            </div>
          </div>

          <Surface className="flex flex-col justify-between bg-slate-50 p-5 ring-slate-200/80">
            <h5 className="text-sm font-semibold text-slate-800">What to expect</h5>
            <ol className="mt-3 list-decimal list-inside space-y-2 text-sm text-slate-700">
              <li>A focused 20-minute call to learn your goals and outline next steps — no pressure, just clarity.</li>
              <li>You’ll receive a recap email with a secure way to send information and documents.</li>
              <li>We review what you have today and build a plan aligned to your goals and risk comfort.</li>
              <li>After approval, we launch your plan and monitor progress.</li>
              <li>We meet through the year to review goals and life events and adjust as needed.</li>
            </ol>
            <div className="mt-5">
              <Button href="/contact" variant="secondary" className="w-full">
                Get Started Today
              </Button>
            </div>
          </Surface>
        </div>
      </Surface>
    </div>
  );
}

export default function ServicesTabs() {
  const [active, setActive] = useState(SERVICES[0].id);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const id = window.location.hash?.slice(1);
    if (SERVICES.some((service) => service.id === id)) {
      setActive(id);
    }
  }, []);

  return (
    <div>
      <PillTabs tabs={SERVICES} value={active} onChange={setActive} ariaLabel="Service selector" />
      <ServicePanel id={active} />
    </div>
  );
}
